<template>
  <div class="upcoming-meal-selection">
    <b-row align-h="center" justify="center" class="mb-3">
      <b-col cols="12" md="8">
        <p class="text-center text-muted">
          Make your selections by {{ formatCutoffDateTime(cutoffDateTime) }}
        </p>
      </b-col>
    </b-row>

    <b-row v-if="loading" key="loading" class="text-center pt-3">
      <b-col cols="12">
        <Spinner :size="100" centered />
      </b-col>
    </b-row>

    <div v-else>
      <b-row align-h="center" class="mb-4">
        <b-col cols="12" md="6">
          <b-card class="delivery-details">
            <h4>Delivery Details</h4>
            <div class="delivery-info">
              <p>
                <strong>Subscription:</strong> {{ rotatingSubscription.name }}
              </p>
              <p>
                <strong>Delivery Date:</strong>
                {{ formatDateTime(scheduledDateTime) }}
              </p>
              <p>
                <strong>Selection Deadline:</strong>
                {{ formatDateTime(cutoffDateTime) }}
              </p>
              <p>
                <strong>Required Selections:</strong>
                {{ rotatingSubscription.min_items }} -
                {{ rotatingSubscription.max_items }} meals
              </p>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <b-row align-h="center" class="mb-3">
        <b-col cols="12" md="8">
          <b-alert
            :show="isPastCutoffTime"
            variant="warning"
            class="text-center"
          >
            The selection deadline has passed. Any changes you make now may not
            be reflected in your order.
          </b-alert>
        </b-col>
      </b-row>

      <b-row align-h="center" class="mb-3">
        <b-col cols="12" md="8">
          <b-alert
            :show="saveSuccess"
            variant="success"
            dismissible
            class="text-center"
            @dismissed="saveSuccess = false"
          >
            Your meal selections have been saved successfully!
          </b-alert>
        </b-col>
      </b-row>

      <b-row align-h="center" class="mb-3">
        <b-col cols="12" md="8">
          <b-alert
            :show="saveError"
            variant="danger"
            dismissible
            class="text-center"
            @dismissed="saveError = false"
          >
            {{ errorMessage }}
          </b-alert>
        </b-col>
      </b-row>

      <ProductGrid
        v-if="products.length > 0"
        :products="products"
        :rotating-subscription="rotatingSubscription"
        :meals-selected="mealsSelected"
        :can-edit="!isPastCutoffTime"
        @add-to-cart="addToSelection"
        @toggle-favorite="toggleFavorite"
      />

      <b-row v-else class="text-center no-products pt-3">
        <b-col cols="12">
          <h3>No products available for selection</h3>
          <p>Please check back later or contact customer service.</p>
        </b-col>
      </b-row>

      <b-row align-h="center" class="mt-4">
        <b-col cols="12" md="6" class="d-flex justify-content-center">
          <b-button
            variant="primary"
            size="lg"
            :disabled="!isSelectionValid || saving || isPastCutoffTime"
            class="w-100"
            @click="saveSelections"
          >
            <b-spinner v-if="saving" small />
            <span v-else>Save Selections</span>
          </b-button>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import ProductGrid from '@/shop/rotating-subscription-partials/ProductGrid.vue';
import Spinner from '@/common/Spinner.vue';
import {
  fetchRotatingSubscriptionProducts,
  customerSubscriptionProductSelection,
  hasMealSelectionForWeek,
  validateRotatingSubscriptionOrder,
  calculateMealsSelected,
  formatSubscriptionProducts,
  getCustomerSubscriptionProductSelection,
} from '@/subscriptions/rotating-subscription-partials/rotating-subscription-service';

export default {
  name: 'UpcomingSubscriptionMealSelection',
  components: {
    ProductGrid,
    Spinner,
  },
  props: {
    rotatingSubscription: {
      type: Object,
      required: true,
    },
    subscription: {
      type: Object,
      required: true,
    },
    scheduledDateTime: {
      type: String,
      required: true,
    },
    cutoffDateTime: {
      type: String,
      required: true,
    },
    subscriptionDay: {
      type: Number,
      required: true,
    },
    subscriptionTime: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      products: [],
      selectedProducts: {},
      loading: true,
      saving: false,
      saveSuccess: false,
      saveError: false,
      errorMessage: '',
    };
  },
  computed: {
    ...mapGetters(['user', 'region', 'vendor']),
    mealsSelected() {
      return calculateMealsSelected(this.selectedProducts);
    },
    isSelectionValid() {
      return validateRotatingSubscriptionOrder(
        this.mealsSelected,
        this.rotatingSubscription.min_items,
        this.rotatingSubscription.max_items
      );
    },
    isPastCutoffTime() {
      return moment().isAfter(moment(this.cutoffDateTime));
    },
  },
  async created() {
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      try {
        this.loading = true;

        // Get products for this rotating subscription
        const productsResponse = await fetchRotatingSubscriptionProducts({
          rotationUuid: this.rotatingSubscription.uuid,
          availableAt: this.scheduledDateTime,
        });

        // Check if customer has already made selections for this time slot
        const selectionsResponse =
          await getCustomerSubscriptionProductSelection({
            rotationUuid: this.rotatingSubscription.uuid,
            scheduled: this.scheduledDateTime,
          });

        // Format products with existing selections if available
        this.products = formatSubscriptionProducts(
          productsResponse,
          selectionsResponse || []
        );

        // Initialize selectedProducts with any existing selections
        this.selectedProducts = {};
        if (selectionsResponse && selectionsResponse.length > 0) {
          selectionsResponse.forEach((selection) => {
            const product = this.products.find(
              (p) => p.id === selection.product_id
            );
            if (product) {
              this.selectedProducts[product.product_uuid] = {
                ...product,
                quantity: selection.quantity || 0,
              };
            }
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        this.errorMessage = 'Failed to load products. Please try again later.';
        this.saveError = true;
      } finally {
        this.loading = false;
      }
    },
    formatDateTime(dateTimeString) {
      return moment(dateTimeString).format('dddd, MMMM D, YYYY [at] h:mm A');
    },
    formatCutoffDateTime(dateTimeString) {
      return moment(dateTimeString).format('dddd, MMMM D [at] h:mm A');
    },
    addToSelection(product) {
      if (product.quantity === 0) {
        this.$delete(this.selectedProducts, product.product_uuid);
      } else {
        this.$set(this.selectedProducts, product.product_uuid, product);
      }
    },
    async saveSelections() {
      if (!this.isSelectionValid) {
        return;
      }

      try {
        this.saving = true;
        this.saveError = false;

        for (const product of Object.values(this.selectedProducts)) {
          const payload = {
            customer_id: this.user.id,
            rotating_subscription_id: this.rotatingSubscription.id,
            rotating_subscription_product_id: product.id,
            subscription_day: this.subscriptionDay,
            subscription_time: this.subscriptionTime,
            order_product_id: product.order_product_id || null,
            quantity: product.quantity || 1,
          };

          // Add modifiers if available
          if (
            product.product_modifiers &&
            product.product_modifiers.length > 0
          ) {
            payload.modifiers = [];

            // Flatten modifiers structure and transform it to the required format
            product.product_modifiers.forEach((modifierGroup) => {
              if (modifierGroup.items && Array.isArray(modifierGroup.items)) {
                modifierGroup.items.forEach((item) => {
                  if (item.item_quantity > 0) {
                    payload.modifiers.push({
                      modifier_item_id: item.id,
                      quantity: item.item_quantity,
                    });
                  }
                });
              }
            });
          }

          await customerSubscriptionProductSelection({
            data: payload,
          });
        }

        this.saveSuccess = true;
      } catch (error) {
        console.error('Error saving selections:', error);

        // Get a more detailed error message
        const errorMessage =
          error.response?.data?.message ||
          'Failed to save your selections. Please try again.';
        console.error('Full error details:', {
          message: errorMessage,
          data: error.response?.data,
          status: error.response?.status,
        });

        this.errorMessage = errorMessage;
        this.saveError = true;
      } finally {
        this.saving = false;
      }
    },
    toggleFavorite(product) {
      // If the product is in selectedProducts, update its favorite status
      if (this.selectedProducts[product.product_uuid]) {
        this.selectedProducts[product.product_uuid].favorite = product.favorite;
      }

      // Also update in the products array
      const index = this.products.findIndex((p) => p.id === product.id);
      if (index !== -1) {
        this.$set(this.products, index, {
          ...this.products[index],
          favorite: product.favorite,
        });
      }

      // Call API to save favorite status (if needed)
      // Implement API call to update favorite status in database
    },
  },
};
</script>

<style lang="scss" scoped>
.upcoming-meal-selection {
  padding: 20px 0;
}

.delivery-details {
  background-color: #f8f9fa;

  h4 {
    margin-bottom: 15px;
    color: #333;
  }

  .delivery-info {
    p {
      margin-bottom: 8px;

      strong {
        color: #209a32;
      }
    }
  }
}

.no-products {
  padding: 50px 0;
  background-color: #f8f9fa;
  border-radius: 8px;

  h3 {
    color: #333;
    margin-bottom: 15px;
  }

  p {
    color: #666;
  }
}
</style>
